import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, Check, Globe } from "lucide-react";
import { COUNTRIES, countryName, flagEmoji } from "../../utils/countries";

interface Props {
  value: string; // selected ISO alpha-2 code, or ""
  onChange: (code: string) => void;
  // Codes already used elsewhere — shown with a subtle "added" hint but still
  // selectable (selecting one replaces the existing rule upstream).
  usedCodes?: string[];
  placeholder?: string;
}

// A searchable country picker — type to filter ~190 countries instead of
// scrolling a giant native <select>. Closes on outside-click or Escape.
const CountrySelect = ({
  value,
  onChange,
  usedCodes = [],
  placeholder = "Search country…",
}: Props) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const used = useMemo(
    () => new Set(usedCodes.map((c) => c.toUpperCase())),
    [usedCodes],
  );

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [query]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Reset the highlighted row whenever the filtered set changes.
  useEffect(() => setActive(0), [query]);

  // Keep the highlighted row scrolled into view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.children[active] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const choose = (code: string) => {
    onChange(code);
    setQuery("");
    setOpen(false);
  };

  const openMenu = () => {
    setOpen(true);
    setQuery("");
    // focus the search field on the next tick (after it mounts)
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const pick = results[active];
      if (pick) choose(pick.code);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  const flag = flagEmoji(value);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => (open ? setOpen(false) : openMenu())}
        className={`w-full flex items-center gap-2 px-2.5 h-9 bg-white border rounded-lg text-sm text-left transition-colors ${
          open
            ? "border-brand-500 ring-2 ring-brand-500/20"
            : "border-ink-200 hover:border-ink-300"
        }`}
      >
        {value ? (
          <span className="text-base leading-none">{flag || "🏳️"}</span>
        ) : (
          <Globe size={14} className="text-ink-400 shrink-0" />
        )}
        <span
          className={`flex-1 truncate ${value ? "text-ink-800" : "text-ink-400"}`}
        >
          {value ? countryName(value) : placeholder}
        </span>
        <ChevronDown
          size={14}
          className={`text-ink-400 shrink-0 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-ink-200 rounded-xl shadow-lg overflow-hidden">
          <div className="relative border-b border-ink-100">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-400"
            />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Type a country name…"
              className="w-full pl-8 pr-2.5 h-9 text-sm focus:outline-none placeholder:text-ink-400"
            />
          </div>
          <div ref={listRef} className="max-h-56 overflow-y-auto py-1">
            {results.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-ink-400">
                No countries match “{query}”
              </div>
            ) : (
              results.map((c, i) => {
                const selected = c.code === value;
                const isUsed = used.has(c.code);
                return (
                  <button
                    key={c.code}
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose(c.code)}
                    className={`w-full flex items-center gap-2.5 px-3 h-9 text-sm text-left transition-colors ${
                      i === active ? "bg-brand-50/70" : "bg-white"
                    }`}
                  >
                    <span className="text-base leading-none w-5 text-center">
                      {flagEmoji(c.code)}
                    </span>
                    <span className="flex-1 truncate text-ink-700">
                      {c.name}
                    </span>
                    {isUsed && !selected && (
                      <span className="text-[10px] text-ink-400 uppercase tracking-wide">
                        Added
                      </span>
                    )}
                    <span className="text-[11px] font-mono text-ink-400">
                      {c.code}
                    </span>
                    {selected && (
                      <Check size={13} className="text-brand-600 shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelect;
